import { FC, FocusEvent, useCallback, useRef, useState } from "react";
import { useEffect } from "react";
import { Route, Routes, useLocation } from "react-router";

import { Button, ButtonAppearance, Buttons, Link, Tooltip } from "capitalrx-components-2.0/lib/general";
import * as Color from "capitalrx-components-2.0/lib/general/Color";
import { Grid, Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Dashboard } from "capitalrx-types";
import classNames from "classnames";

import { useLayout } from "../../ContentLayout";
import { useContentChannel } from "../../components/ContentChannel";
import { useShellModulePathResolver } from "../../hooks";
import { useAuthToken } from "../../hooks";
import { Actions } from "./Actions";
import { Logo } from "./Logo";
import { Menu } from "./Menu";
import { Notifications } from "./Notifications";
import { getNavigationGroupClassName } from "./getNavigationGroupClassName";
import { useMenuGroups, useMenuItems } from "./hooks";

import styles from "./MenuBar.module.css";

const MenuButtonAppearance: ButtonAppearance = {
  ...Buttons.Functional.Tertiary.Large,

  normal: {
    ...Buttons.Functional.Tertiary.Large.normal,
    foregroundColor: Color.White,
  },
  hover: {
    ...Buttons.Functional.Tertiary.Large.hover,
    foregroundColor: Color.Turqoise["100"],
  },
  pressed: {
    ...Buttons.Functional.Tertiary.Large.pressed,
    foregroundColor: Color.White,
  },
};

const ExpandedMenuButtonAppearance: ButtonAppearance = {
  ...MenuButtonAppearance,
  normal: {
    ...Buttons.Functional.Tertiary.Large.normal,
    foregroundColor: Color.Turqoise["100"],
  },
};

interface MenuTitleRouteData {
  readonly path?: string;
  readonly absolutePath?: boolean;
}

export function getMenuTitleRoutePattern<TModule>(
  module: TModule,
  data: MenuTitleRouteData,
  resolvePath: (module: TModule, path: string | undefined) => string | undefined,
): string {
  const { path, absolutePath = false } = data;

  if (!absolutePath) {
    const moduleRoot = resolvePath(module, "/") ?? "/";
    return moduleRoot.endsWith("/") ? `${moduleRoot}*` : `${moduleRoot}/*`;
  }

  let resolvedPath = path;
  if (resolvedPath && resolvedPath.startsWith("~")) {
    resolvedPath = resolvedPath.substring(1);
  }

  resolvedPath = resolvedPath ?? "/";

  return resolvedPath.endsWith("/") ? `${resolvedPath}*` : `${resolvedPath}/*`;
}

/**
 * The shell main menu bar component.
 */
export const MenuBar: FC = () => {
  const [expanded, setExpanded] = useState(false);
  const buttonContainerRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const location = useLocation();
  const resolvePath = useShellModulePathResolver();
  const actionBars = useContentChannel(null, "app-action-bars");
  const layoutType = useLayout();

  const { menuColumnSpan, nameVisible } = useResponsive({
    menuColumnSpan: { desktop: 3, tablet: 6, mobile: 12 },
    nameVisible: { desktop: true, tablet: true, mobile: false },
  });

  const [authToken, _] = useAuthToken();
  let identity = "";

  if (authToken) {
    const parts = authToken.split(".");
    const payload = JSON.parse(atob(parts[1]));
    identity = payload.username;
  }

  const items = useMenuItems();
  const columnGroups = useMenuGroups(items, 12 / menuColumnSpan);

  const clickHandler = useCallback(() => {
    setExpanded((previousExpanded) => !previousExpanded);

    if (buttonContainerRef.current) {
      buttonContainerRef.current.focus(); // Ensures the button gets the focus.
    }
  }, []);

  const mouseDownHandler = useCallback((ev: React.MouseEvent): void => {
    ev.preventDefault();
  }, []);

  const blurHandler = useCallback((ev: FocusEvent): void => {
    const { relatedTarget } = ev;
    if (relatedTarget) {
      if (relatedTarget === buttonContainerRef?.current) return;
      if (relatedTarget === menuRef?.current || (menuRef.current && menuRef.current.contains(relatedTarget as Node))) return;
    }

    setExpanded(false);
  }, []);

  useEffect(() => {
    function listener(): void {
      setExpanded(false);
    }

    if (expanded) {
      document.addEventListener("scroll", listener);
      globalThis.addEventListener("resize", listener);
    }

    return (): void => {
      document.removeEventListener("scroll", listener);
      globalThis.removeEventListener("resize", listener);
    };
  }, [expanded]);

  useEffect(() => {
    setExpanded(false);
  }, [location]);

  if (layoutType.startsWith("fullscreen")) {
    return null;
  }

  const columnElements = columnGroups.map((groups, columnIndex) => {
    return (
      <Grid.Span key={columnIndex} columns={menuColumnSpan}>
        <Stack className="column" key={columnIndex} spacing="large">
          {groups.map((group) => {
            const groupData = Dashboard.NavigationGroup[group.id] ?? Dashboard.NavigationGroup.Unknown;

            return (
              <Stack className={classNames("group", getNavigationGroupClassName(groupData))} key={groupData.id} spacing="mini">
                <h3 className="header" style={{ color: groupData.color }}>
                  {groupData.name}
                </h3>
                <Stack spacing="mini">
                  {group.items.map((item, index) => {
                    const { module, data } = item;
                    const { path, testId, title, beta = false, absolutePath = false, internalUseOnly = false } = data;

                    const resolvedPath = absolutePath ? (path ?? "/") : (resolvePath(module, path) ?? "/");
                    let canViewItem = true;

                    if (internalUseOnly && !identity.endsWith("@cap-rx.com")) {
                      canViewItem = false;
                    }

                    if (canViewItem) {
                      return (
                        <Shelf key={index} spacing="mini">
                          <Link to={resolvedPath} testId={testId} tabIndex={2}>
                            {title}
                          </Link>
                          {beta && <span className="beta">BETA</span>}
                          <Shelf.Spacer />
                        </Shelf>
                      );
                    }
                  })}
                </Stack>
              </Stack>
            );
          })}
        </Stack>
      </Grid.Span>
    );
  });

  return (
    <nav data-testid="menubar" className={classNames("menu-bar", styles.menuBarRoot, { [styles.hasActionBars]: actionBars.length > 0 })}>
      <div className="bar-container" tabIndex={-1} onBlur={blurHandler}>
        <Shelf className="bar" alignment="center">
          <div
            className="menu-button"
            ref={buttonContainerRef}
            onClickCapture={clickHandler}
            onBlurCapture={blurHandler}
            onMouseDown={mouseDownHandler}
            tabIndex={1}
          >
            <Button icon="menu-grid" appearance={expanded ? ExpandedMenuButtonAppearance : MenuButtonAppearance} tabIndex={-1} />
          </div>
          <Logo />
          {nameVisible && (
            <Shelf alignment="span">
              <Routes>
                {items.map((item, index) => {
                  const { data, module } = item;
                  const { title, description, path, absolutePath = false } = data;
                  const resolvedPath = getMenuTitleRoutePattern(module, { path, absolutePath }, resolvePath);

                  return (
                    <Route
                      key={index}
                      path={resolvedPath}
                      element={
                        <Tooltip content={description} alignment="bottom">
                          {(ref) => (
                            <div className="module-name" ref={ref}>
                              {title}
                            </div>
                          )}
                        </Tooltip>
                      }
                    />
                  );
                })}
                <Route path="*" element={null} />
              </Routes>
            </Shelf>
          )}
          <Shelf.Spacer />
          <Shelf spacing="none">
            <Notifications />
            <Actions />
          </Shelf>
        </Shelf>

        <Menu ref={menuRef} expanded={expanded}>
          <Grid className={styles.menuGridRoot} spacing="none">
            {columnElements}
          </Grid>
        </Menu>
      </div>
    </nav>
  );
};
